import {
  type Color,
  DataTexture,
  DoubleSide,
  FrontSide,
  MeshToonMaterial,
  NearestFilter,
  RedFormat,
  type Texture,
  UnsignedByteType,
  Vector2,
  type WebGLProgramParametersWithUniforms,
} from 'three';

/** Outline classes written to the alpha of the normal buffer and read by the post pass (05 §8). */
export const OUTLINE = {
  none: 0,
  prop: 1,
  character: 2,
  interactive: 3,
  alerted: 4,
  /** The player's ranger: gets a light rim instead of an ink outline at night (05 §4.3). */
  player: 5,
} as const;
export type OutlineClass = (typeof OUTLINE)[keyof typeof OUTLINE];

/** Uniforms every world-anchored pattern needs; one shared instance, updated by the pipeline. */
export const sharedUniforms = {
  uCamTexel: { value: new Vector2() },
  uTime: { value: 0 },
};

/**
 * Toon ramp indexed by half-Lambert (N·L * 0.5 + 0.5). Faces pointing away from the light
 * get nothing from it (ambient only); lit faces fall into four bands.
 */
export function createGradientMap(
  steps: readonly number[] = [0, 0, 0, 0, 0.34, 0.6, 0.85, 1],
): DataTexture {
  const data = new Uint8Array(steps.map((s) => Math.round(s * 255)));
  const tex = new DataTexture(data, steps.length, 1, RedFormat, UnsignedByteType);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

export type Shader = WebGLProgramParametersWithUniforms;

const MRT_DECL = /* glsl */ `
layout(location = 1) out highp vec4 gNormalId;
uniform float uOutlineClass;
`;

const MRT_WRITE = /* glsl */ `
  gNormalId = vec4(normalize(normal) * 0.5 + 0.5, uOutlineClass);
`;

/** Adds the second render-target output (view normal + outline class) to a built-in material. */
export function patchMrt(shader: Shader, outlineClass: OutlineClass): void {
  shader.uniforms['uOutlineClass'] = { value: outlineClass / 8 };
  shader.fragmentShader = shader.fragmentShader
    .replace('void main() {', `${MRT_DECL}\nvoid main() {`)
    .replace('#include <dithering_fragment>', `#include <dithering_fragment>\n${MRT_WRITE}`);
}

export interface ToonOptions {
  gradientMap: Texture;
  outline: OutlineClass;
  doubleSided?: boolean;
  /** Emissive colour (muzzle flashes, lit windows, fire): bypasses lighting. */
  emissive?: Color;
  emissiveIntensity?: number;
}

/** Standard vertex-coloured toon material for every prop, structure and character. */
export function createToonMaterial(opts: ToonOptions): MeshToonMaterial {
  const material = new MeshToonMaterial({
    vertexColors: true,
    gradientMap: opts.gradientMap,
    side: opts.doubleSided ? DoubleSide : FrontSide,
  });
  if (opts.emissive) {
    material.emissive.copy(opts.emissive);
    material.emissiveIntensity = opts.emissiveIntensity ?? 1;
  }
  material.onBeforeCompile = (shader) => patchMrt(shader, opts.outline);
  material.customProgramCacheKey = () => `toon-mrt`;
  return material;
}

/* -------------------------------------------------------------------------------------- */

export const NOISE_GLSL = /* glsl */ `
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), u.x),
             mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), u.x), u.y);
}
`;

export interface TerrainMaterialOptions {
  gradientMap: Texture;
  /** RGBA weights of the four ground materials, linear-filtered, one texel per half metre. */
  splat: Texture;
  /** World x/z of the splat texture's (0,0) corner and its size in metres. */
  mapOrigin: Vector2;
  mapSize: Vector2;
  /** 4 materials x 4 ramp steps, dark to light, linear colour. */
  ramps: readonly Color[];
  /** Ramp step each material normally uses (0..3). */
  baseSteps: readonly [number, number, number, number];
}

/**
 * Ground material. Albedo is computed per pixel from the splat map: the winning material
 * is chosen with a world-anchored dither so borders look hand-dabbed, then a low-frequency
 * noise moves between neighbouring ramp steps and a sparse hash adds single-pixel specks.
 * All patterns are anchored to the world texel grid so nothing swims while scrolling.
 */
export function createTerrainMaterial(opts: TerrainMaterialOptions): MeshToonMaterial {
  const material = new MeshToonMaterial({ gradientMap: opts.gradientMap });
  const rampValues = opts.ramps.flatMap((c) => [c.r, c.g, c.b]);

  material.onBeforeCompile = (shader) => {
    patchMrt(shader, OUTLINE.none);
    shader.uniforms['uSplat'] = { value: opts.splat };
    shader.uniforms['uMapOrigin'] = { value: opts.mapOrigin };
    shader.uniforms['uMapSize'] = { value: opts.mapSize };
    shader.uniforms['uRamps'] = { value: rampValues };
    shader.uniforms['uBaseSteps'] = { value: opts.baseSteps };
    shader.uniforms['uCamTexel'] = sharedUniforms.uCamTexel;

    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying vec3 vWorldPos;\nvoid main() {')
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n  vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        /* glsl */ `
varying vec3 vWorldPos;
uniform sampler2D uSplat;
uniform vec2 uMapOrigin;
uniform vec2 uMapSize;
uniform vec3 uRamps[16];
uniform vec4 uBaseSteps;
uniform vec2 uCamTexel;
${NOISE_GLSL}
vec3 terrainAlbedo() {
  vec4 w = texture2D(uSplat, (vWorldPos.xz - uMapOrigin) / uMapSize);
  vec2 cell = floor(gl_FragCoord.xy) + uCamTexel;
  float pixelHash = hash12(cell);

  // Winner and runner-up among the four materials.
  int first = 0; float w1 = w.r;
  if (w.g > w1) { first = 1; w1 = w.g; }
  if (w.b > w1) { first = 2; w1 = w.b; }
  if (w.a > w1) { first = 3; w1 = w.a; }
  int second = first; float w2 = -1.0;
  float ws[4] = float[4](w.r, w.g, w.b, w.a);
  for (int i = 0; i < 4; i++) {
    if (i != first && ws[i] > w2) { second = i; w2 = ws[i]; }
  }
  float wobble = valueNoise(vWorldPos.xz * 0.8) - 0.5;
  float edge = w1 - w2;
  int mat = edge < (pixelHash - 0.5) * 0.22 + wobble * 0.30 + 0.02 ? second : first;

  // Tonal variation inside a material: broad patches plus rare single-pixel specks.
  float patchNoise = valueNoise(vWorldPos.xz * 0.23 + float(mat) * 17.3);
  float fine = valueNoise(vWorldPos.xz * 1.7 + float(mat) * 5.1);
  float tone = patchNoise * 0.7 + fine * 0.3;
  float stepIndex = uBaseSteps[mat];
  if (tone > 0.64) stepIndex += 1.0;
  if (tone < 0.34) stepIndex -= 1.0;
  float speck = hash12(cell + 91.7);
  if (speck > 0.988) stepIndex += 1.0;
  if (speck < 0.014) stepIndex -= 1.0;
  stepIndex = clamp(stepIndex, 0.0, 3.0);
  return uRamps[mat * 4 + int(stepIndex)];
}
void main() {`,
      )
      .replace('#include <color_fragment>', 'diffuseColor.rgb = terrainAlbedo();');
  };
  material.customProgramCacheKey = () => 'toon-mrt-terrain';
  return material;
}

/* -------------------------------------------------------------------------------------- */

/**
 * Self-lit surfaces (lit windows, lamp lenses, muzzle flashes, fire): the vertex colour is
 * added as emission on top of normal lighting, so they hold their colour at night.
 */
export function createEmissiveMaterial(gradientMap: Texture, boost = 1.6): MeshToonMaterial {
  const material = new MeshToonMaterial({ vertexColors: true, gradientMap });
  material.onBeforeCompile = (shader) => {
    patchMrt(shader, OUTLINE.none);
    shader.uniforms['uBoost'] = { value: boost };
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform float uBoost;\nvoid main() {')
      .replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vColor.rgb * uBoost;',
      );
  };
  material.customProgramCacheKey = () => 'toon-mrt-emissive';
  return material;
}

/** MRT patch for unlit materials (points, lines): no outline, camera-facing normal. */
export function patchMrtUnlit(shader: Shader): void {
  shader.fragmentShader = shader.fragmentShader
    .replace('void main() {', 'layout(location = 1) out highp vec4 gNormalId;\nvoid main() {')
    .replace(
      '#include <premultiplied_alpha_fragment>',
      '#include <premultiplied_alpha_fragment>\n  gNormalId = vec4(0.5, 0.5, 1.0, 0.0);',
    );
}
